/*
  Transformer Table landing page behaviour (templates/page.transformer-table.json). Loaded once,
  deferred, by sections/tt-page.liquid, which also emits the page config as JSON (#tt-config):
    endpoint        -- the lead endpoint, https://adju.com/api/leads (SposMobileSite
                       api/leads/LeadsController; spec: SPOS repo docs/handoff/customer-leads)
    adsId           -- the Google Ads account tag, AW-... (empty = Google Ads stays off)
    leadLabel       -- conversion label fired when a lead is accepted
    callLabel       -- conversion label fired on a tap/click of any tel: link
    forwardingLabel -- Google forwarding-number label (website call conversions)
    phoneDisplay    -- the page phone number exactly as printed (the forwarding swap needs it)
    enhanced        -- send the lead's phone/email with the conversion (Google hashes it)
  The OpenAI (ChatGPT) Ads pixel is not configured here: snippets/openai-pixel.liquid loads it
  site-wide from the theme setting "OpenAI pixel ID", and this script only calls window.oaiq
  when it exists (lead_created when a lead is accepted, a phone_tap custom event on tel: taps).

  Plain JavaScript, no dependencies. Every handler is delegated from the document and scoped to
  the tt-* markup, so it keeps working when the theme editor re-renders the section.

  The lead form, attribution and Google Ads code mirror assets/bc-landing.js (the Best Chest
  page): a fix to one usually belongs in the other.
*/
(function () {
  'use strict';

  if (window.ttLandingLoaded) return;
  window.ttLandingLoaded = true;

  var config = {};
  try {
    var cfgEl = document.getElementById('tt-config');
    if (cfgEl) config = JSON.parse(cfgEl.textContent || '{}');
  } catch (e) {
    config = {};
  }

  function closest(target, selector) {
    return target && target.closest ? target.closest(selector) : null;
  }

  /* ================= the "How it works" drawing =================
     CONFIRMED figures: the collection sizes and seat counts below are the manufacturer's and
     were signed off with the design (2026-09-26). Do not round or tidy them -- Nova really is
     43.9 in closed and 104.1 in open.

     All sizes are inches, seen from above; every extension panel is 20 in. `lengths` and
     `seats` are indexed by the number of panels in; `steps` maps the four occasions (Just us,
     Friends over, Family dinner, The holidays) to a number of panels. `note` is the desktop
     caption, `noteShort` the phone one. */
  var PANEL = 20;
  var COLLECTIONS = {
    nova: {
      name: 'Nova',
      width: 43.9,
      corner: 4,
      steps: [0, 1, 2, 3],
      lengths: [43.9, 63.9, 83.9, 104.1],
      seats: ['4', '6', '8', '10'],
      note: 'Nova is a 43.9 in square when closed and opens to 104.1 in with three panels.',
      noteShort: 'Nova opens from a 43.9 in square to 104.1 in with three panels.',
    },
    round: {
      name: 'Round',
      width: 45,
      corner: 22.5,
      steps: [0, 1, 2, 3],
      lengths: [45, 65, 85, 105],
      seats: ['4', '6', '8', '10'],
      note: 'Round is a 45 in circle when closed and opens to a 105 in oval with three panels.',
      noteShort: 'Round opens from a 45 in circle to a 105 in oval with three panels.',
    },
    gather: {
      name: 'Gather',
      width: 48,
      corner: 1.5,
      steps: [0, 1, 2, 4],
      lengths: [72, 92, 112, 132, 152],
      seats: ['6', '8', '10', '12', '16'],
      note: 'Gather is 48 in wide and opens from 72 in to 152 in with four panels.',
      noteShort: 'Gather is 48 in wide and opens from 72 in to 152 in with four panels.',
    },
    savor: {
      name: 'Savor',
      width: 48,
      corner: 1.5,
      steps: [0, 1, 2, 4],
      lengths: [72, 92, 112, 132, 152],
      seats: ['6', '8', '10', '12', '16'],
      note: 'Savor is 48 in wide and opens from 72 in to 152 in with four panels.',
      noteShort: 'Savor is 48 in wide and opens from 72 in to 152 in with four panels.',
    },
  };
  var STAGE_CAPTIONS = [
    'Compact for everyday meals.',
    'A panel or two when company comes.',
    'Add panels as the guest list grows.',
    'Fully extended for the big gathering.',
  ];
  var DEFAULT_COLLECTION = 'gather';
  var DEFAULT_STAGE = 3;

  // the drawing is scaled so the longest table of all just fits, and the row is as tall as the widest
  var LONGEST = 0;
  var WIDEST = 0;
  var MOST_PANELS = 0;
  Object.keys(COLLECTIONS).forEach(function (key) {
    var c = COLLECTIONS[key];
    // drawn length: the two halves plus the panels (the label uses the stated length)
    var drawn = c.lengths[0] + PANEL * (c.lengths.length - 1);
    LONGEST = Math.max(LONGEST, drawn, c.lengths[c.lengths.length - 1]);
    WIDEST = Math.max(WIDEST, c.width);
    MOST_PANELS = Math.max(MOST_PANELS, c.lengths.length - 1);
  });

  function lengthLabel(inches) {
    var whole = Math.round(inches);
    var feet = Math.floor(whole / 12);
    var rem = whole % 12;
    return inches + ' in long (' + feet + ' ft' + (rem ? ' ' + rem + ' in' : '') + ')';
  }

  function setText(root, selector, text) {
    var el = root.querySelector(selector);
    if (el) el.textContent = text;
  }

  function drawDiagram(diagram, announce) {
    var key = diagram.getAttribute('data-tt-collection');
    if (!COLLECTIONS.hasOwnProperty(key)) key = DEFAULT_COLLECTION;
    var c = COLLECTIONS[key];
    var stage = parseInt(diagram.getAttribute('data-tt-stage'), 10);
    if (!(stage >= 0 && stage < c.steps.length)) stage = DEFAULT_STAGE;
    var panels = c.steps[stage];
    var seats = c.seats[panels];
    var length = lengthLabel(c.lengths[panels]);

    var select = diagram.querySelector('[data-tt-select]');
    if (select && select.value !== key) select.value = key;
    diagram.querySelectorAll('[data-tt-stage-pick]').forEach(function (btn) {
      btn.setAttribute('aria-pressed', parseInt(btn.getAttribute('data-tt-stage-pick'), 10) === stage ? 'true' : 'false');
    });

    setText(diagram, '[data-tt-panels]', panels === 0 ? 'None' : String(panels));
    setText(diagram, '[data-tt-seats]', seats);
    setText(diagram, '[data-tt-length]', length);
    setText(diagram, '[data-tt-caption]', STAGE_CAPTIONS[stage] + ' ' + c.note + ' Drawn to scale.');
    setText(diagram, '[data-tt-caption-short]', STAGE_CAPTIONS[stage] + ' ' + c.noteShort + ' Drawn to scale.');

    var drawing = diagram.querySelector('[data-tt-drawing]');
    var table = diagram.querySelector('[data-tt-table]');
    if (drawing && table) {
      drawing.style.setProperty('--tt-longest', String(LONGEST));
      drawing.style.setProperty('--tt-widest', String(WIDEST));
      drawing.style.setProperty('--tt-half', String(c.lengths[0] / 2));
      drawing.style.setProperty('--tt-depth', String(c.width));
      drawing.style.setProperty('--tt-corner', String(c.corner));

      // the markup ships a panel piece for each panel the largest table takes; top up if the data outgrows it
      var pieces = table.querySelectorAll('[data-tt-panel]');
      var rightEnd = table.querySelector('[data-tt-end-right]');
      for (var i = pieces.length; i < MOST_PANELS; i++) {
        var piece = document.createElement('span');
        piece.className = 'tt-table__panel';
        piece.setAttribute('data-tt-panel', '');
        table.insertBefore(piece, rightEnd);
      }
      table.querySelectorAll('[data-tt-panel]').forEach(function (el, index) {
        el.classList.toggle('is-in', index < panels);
      });

      var state = panels === 0 ? 'closed, with no panels in' : 'with ' + panels + (panels === 1 ? ' panel in' : ' panels in');
      table.setAttribute('aria-label', 'Top-down drawing of the ' + c.name + ' table ' + state + ': ' + length + ', seats up to ' + seats + '.');
    }

    if (announce) {
      var said = panels === 0 ? 'no panels in' : panels + (panels === 1 ? ' panel in' : ' panels in');
      setText(diagram, '[data-tt-live]', c.name + ', ' + said + ': seats up to ' + seats + ', ' + length + '.');
    }
  }

  document.addEventListener('change', function (e) {
    var select = closest(e.target, '[data-tt-select]');
    if (!select) return;
    var diagram = closest(select, '[data-tt-diagram]');
    if (!diagram) return;
    diagram.setAttribute('data-tt-collection', select.value);
    drawDiagram(diagram, true);
    suggestModel(COLLECTIONS.hasOwnProperty(select.value) ? COLLECTIONS[select.value].name : '');
  });

  document.addEventListener('click', function (e) {
    var btn = closest(e.target, '[data-tt-stage-pick]');
    if (!btn) return;
    var diagram = closest(btn, '[data-tt-diagram]');
    if (!diagram) return;
    diagram.setAttribute('data-tt-stage', btn.getAttribute('data-tt-stage-pick'));
    drawDiagram(diagram, true);
  });

  /* ---------------- the collection explored in the drawing is offered in the form ----------------
     Only until the visitor chooses one in the form themselves. */
  var modelChosenByHand = false;
  document.addEventListener('change', function (e) {
    if (closest(e.target, '#tt-lead-form select[name="model"]')) modelChosenByHand = true;
  });
  function suggestModel(name) {
    if (modelChosenByHand || !name) return;
    var select = document.querySelector('#tt-lead-form select[name="model"]');
    if (!select) return;
    for (var i = 0; i < select.options.length; i++) {
      if (select.options[i].value.toLowerCase() === name.toLowerCase()) {
        select.value = select.options[i].value;
        return;
      }
    }
  }

  /* ================= FAQ: one answer open at a time =================
     Native <details>, so it works without this; the script only closes the others.
     `toggle` does not bubble, hence the capturing listener. */
  document.addEventListener(
    'toggle',
    function (e) {
      var item = e.target;
      if (!item || !item.open || !item.parentNode || !item.matches || !item.matches('[data-tt-faq] > details')) return;
      Array.prototype.forEach.call(item.parentNode.children, function (other) {
        if (other !== item && other.tagName === 'DETAILS') other.open = false;
      });
    },
    true
  );

  /* ================= hero video =================
     Muted and looping. It plays by itself as the page loads (the autoplay attribute), and the
     button lets anyone pause it. The script only steps in where the attribute is not enough:
     markup swapped in after load (the theme editor), and a page opened in a background tab --
     a browser will not start a video nobody can see, so it is started when the tab is shown.
     A video the visitor paused stays paused. */
  function heroVideo(page) {
    var media = page.querySelector('.tt-hero__media');
    return media ? { video: media.querySelector('video'), toggle: media.querySelector('[data-tt-video-toggle]') } : {};
  }

  function syncToggle(video, toggle) {
    if (!video || !toggle) return;
    toggle.classList.toggle('is-paused', video.paused);
    toggle.setAttribute('aria-label', video.paused ? 'Play video' : 'Pause video');
  }

  function initVideo(page) {
    var hero = heroVideo(page);
    var video = hero.video;
    var toggle = hero.toggle;
    if (!video || video.ttReady) return;
    video.ttReady = true;
    video.muted = true;
    if (toggle) {
      toggle.hidden = false;
      video.addEventListener('play', function () {
        syncToggle(video, toggle);
      });
      video.addEventListener('pause', function () {
        syncToggle(video, toggle);
      });
      syncToggle(video, toggle);
    }
    resumeVideo(video);
  }

  function playQuietly(video) {
    var started = video.play && video.play();
    // a refused autoplay (iOS Low Power Mode, data saver) leaves the poster and the play button
    if (started && started.catch) started.catch(function () {});
  }

  function resumeVideo(video) {
    if (video.ttPausedByVisitor || !video.paused || document.visibilityState === 'hidden') return;
    playQuietly(video);
  }

  document.addEventListener('visibilitychange', function () {
    document.querySelectorAll('[data-tt-page]').forEach(function (page) {
      var video = heroVideo(page).video;
      if (video) resumeVideo(video);
      page.querySelectorAll('.tt-slot video').forEach(function (slotVideo) {
        if (slotVideo.ttInView) resumeVideo(slotVideo);
      });
    });
  });

  document.addEventListener('click', function (e) {
    var toggle = closest(e.target, '[data-tt-video-toggle]');
    if (!toggle) return;
    var page = closest(toggle, '[data-tt-page]');
    var video = page ? heroVideo(page).video : null;
    if (!video) return;
    video.ttPausedByVisitor = !video.paused;
    if (video.paused) playQuietly(video);
    else video.pause();
  });

  /* ================= photo-slot videos =================
     A video picked for a photo slot (snippets/tt-slot.liquid) is muted and looping, with its
     own controls. It plays once at least half of it is on screen and pauses when it scrolls
     away, for every visitor, like the hero. One the visitor paused with its controls stays
     paused until they play it again. */
  var slotObserver = null;
  function initSlotVideos(page) {
    if (slotObserver) {
      slotObserver.disconnect();
      slotObserver = null;
    }
    var videos = page.querySelectorAll('.tt-slot video');
    if (!videos.length || !('IntersectionObserver' in window)) return;
    slotObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (en) {
          var video = en.target;
          video.ttInView = en.intersectionRatio >= 0.5;
          if (video.ttInView) resumeVideo(video);
          else if (!video.paused) {
            video.ttScrolledAway = true;
            video.pause();
          }
        });
      },
      { threshold: 0.5 }
    );
    videos.forEach(function (video) {
      if (!video.ttReady) {
        video.ttReady = true;
        video.muted = true;
        video.addEventListener('play', function () {
          video.ttPausedByVisitor = false;
        });
        video.addEventListener('pause', function () {
          // a pause from the controls, not from scrolling away or the tab being hidden
          if (video.ttScrolledAway) video.ttScrolledAway = false;
          else if (document.visibilityState === 'visible') video.ttPausedByVisitor = true;
        });
      }
      slotObserver.observe(video);
    });
  }

  /* ================= Google Ads =================
     The storefront already loads gtag.js (Shopify's Google channel, G- tag); registering the
     Ads account on it is one config call. If gtag is somehow absent, load it for the Ads id. */
  function ensureGtag() {
    if (!config.adsId) return false;
    if (typeof window.gtag !== 'function') {
      window.dataLayer = window.dataLayer || [];
      window.gtag = function () {
        window.dataLayer.push(arguments);
      };
      window.gtag('js', new Date());
      var s = document.createElement('script');
      s.async = true;
      s.src = 'https://www.googletagmanager.com/gtag/js?id=' + encodeURIComponent(config.adsId);
      document.head.appendChild(s);
    }
    return true;
  }

  var adsReady = ensureGtag();
  if (adsReady) {
    window.gtag('config', config.adsId);
    if (config.forwardingLabel && config.phoneDisplay) {
      window.gtag('config', config.adsId + '/' + config.forwardingLabel, {
        phone_conversion_number: config.phoneDisplay,
      });
    }
  }

  function fireConversion(label, extra) {
    if (!adsReady || !label) return;
    var params = { send_to: config.adsId + '/' + label };
    if (extra) for (var k in extra) params[k] = extra[k];
    window.gtag('event', 'conversion', params);
  }

  /* ================= OpenAI (ChatGPT) Ads =================
     The measurement pixel is loaded site-wide by snippets/openai-pixel.liquid when the theme
     setting "OpenAI pixel ID" is set: window.oaiq is then OpenAI's queue function, and the SDK
     drains the queue once it has loaded, so timing does not matter. With the setting empty
     there is no oaiq and every call here is a no-op. A measurement must never break the page. */
  function measure(name, data, options) {
    if (typeof window.oaiq !== 'function') return;
    try {
      window.oaiq('measure', name, data, options);
    } catch (e) {
      /* the pixel's problem, not the visitor's */
    }
  }

  /* One id per lead, made on the first submit and kept through retries (a 400 on a field, a
     timeout after the endpoint already saved), so a resend of the same lead is one event to
     OpenAI, and a server-side Conversions API upload could dedupe against it by the same id. */
  var leadEventId = '';
  function newEventId(prefix) {
    var id = '';
    try {
      if (window.crypto && window.crypto.randomUUID) id = window.crypto.randomUUID();
    } catch (e) {
      /* fall through to the fallback */
    }
    if (!id) id = Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
    return prefix + '-' + id;
  }

  document.addEventListener('click', function (e) {
    if (!closest(e.target, '[data-tt-page] a[href^="tel:"]')) return;
    fireConversion(config.callLabel);
    measure('custom', { type: 'custom' }, { custom_event_name: 'phone_tap' });
  });

  /* ---------------- ad attribution ----------------
     Google Ads auto-tagging puts gclid (or gbraid/wbraid on iOS app traffic) on the landing
     URL; campaigns may add utm_*; a ChatGPT ad click adds oppref. Kept in localStorage for
     CLICK_DAYS (Google's click window) so a later visit in another tab, or one that comes back
     through the store's search, still carries the click that started it (the bc-landing.js
     lesson, 2026-09-29). sessionStorage is the fallback where localStorage is refused. The lead
     endpoint stores it on the task. oppref is stored with the rest but NOT sent: the endpoint
     binds a fixed field list (SPOS customer-leads SPEC) and would drop it; the pixel SDK keeps
     its own copy in a __oppref cookie for 30 days. */
  var ATTR_KEYS = ['gclid', 'gbraid', 'wbraid', 'oppref', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
  var ATTR_STORE = 'tt_attr';
  var CLICK_DAYS = 90;
  var DAY_MS = 86400000;
  var attribution = {};
  var attributionAt = 0; // when the click parameters were captured (ms)

  var stores = [];
  ['localStorage', 'sessionStorage'].forEach(function (name) {
    try {
      if (window[name]) stores.push(window[name]);
    } catch (e) {
      /* storage refused (private mode, blocked site data) */
    }
  });
  function loadAttribution(store) {
    try {
      var saved = JSON.parse(store.getItem(ATTR_STORE) || 'null');
      if (!saved || !saved.v || !(Date.now() - saved.t < CLICK_DAYS * DAY_MS)) return false;
      attribution = saved.v;
      attributionAt = saved.t;
      return true;
    } catch (e) {
      return false;
    }
  }
  function saveAttribution(store) {
    try {
      store.setItem(ATTR_STORE, JSON.stringify({ v: attribution, t: attributionAt }));
      return true;
    } catch (e) {
      return false;
    }
  }
  stores.some(loadAttribution);
  try {
    var params = new URLSearchParams(window.location.search);
    var fresh = false;
    ATTR_KEYS.forEach(function (k) {
      var v = params.get(k);
      if (v) {
        if (!fresh) {
          attribution = {};
          fresh = true;
        }
        attribution[k] = v.slice(0, 200);
      }
    });
    if (fresh) {
      attributionAt = Date.now();
      stores.some(saveAttribution);
    }
  } catch (e) {
    /* keep what we have in memory */
  }

  /* Google's own first-party click cookie, written by the Ads tag on the landing visit
     (_gcl_aw = GCL.<seconds>.<gclid>, or 2.1.k<gclid>$i<seconds>): the last resort for a click
     this page never stored. */
  function cookieGclid() {
    var m = document.cookie.match(/(?:^|;\s*)_gcl_aw=(?:GCL\.\d+\.|2\.1\.k)([\w~-]+(?:\.[\w~-]+)*)/);
    return m ? m[1] : '';
  }

  /* How this visit arrived: the referrer of the tab's FIRST load of this page, so a reload or a
     trip to a product page and back does not replace it. A store search shows as /search. */
  var entryReferrer = document.referrer || '';
  try {
    var savedReferrer = sessionStorage.getItem('tt_ref');
    if (savedReferrer === null) sessionStorage.setItem('tt_ref', entryReferrer);
    else entryReferrer = savedReferrer;
  } catch (e) {
    /* storage refused: this load's referrer stands */
  }

  /* ================= sticky call / book bar =================
     Appears once the hero has scrolled away; steps aside while the form itself is on screen. */
  var stickyObserver = null;
  function initSticky(page) {
    if (stickyObserver) {
      stickyObserver.disconnect();
      stickyObserver = null;
    }
    var sticky = page.querySelector('[data-tt-sticky]');
    var hero = page.querySelector('[data-tt-hero]');
    var visit = page.querySelector('#tt-visit');
    if (!sticky || !('IntersectionObserver' in window)) return;
    var heroVisible = true;
    var visitVisible = false;
    var update = function () {
      var show = !heroVisible && !visitVisible;
      sticky.classList.toggle('is-visible', show);
      sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
      sticky.querySelectorAll('a').forEach(function (a) {
        if (show) a.removeAttribute('tabindex');
        else a.setAttribute('tabindex', '-1');
      });
    };
    stickyObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (en.target === hero) heroVisible = en.isIntersecting;
        if (en.target === visit) visitVisible = en.isIntersecting;
      });
      update();
    });
    if (hero) stickyObserver.observe(hero);
    else heroVisible = false;
    if (visit) stickyObserver.observe(visit);
    update();
  }

  /* ================= header phone + booking button (desktop) =================
     Printed by sections/tt-page.liquid, shown in the store header, which is another section:
     a copy goes into the header's grid. Replaced, not added to, each time, so the theme
     editor re-rendering either section leaves one current copy. */
  function placeHeaderCta() {
    var header = document.querySelector('.section-header .header');
    if (!header) return;
    var placed = header.querySelector('[data-tt-header-cta]');
    if (placed) placed.parentNode.removeChild(placed);
    var source = document.querySelector('.tt-page-section [data-tt-header-cta]');
    if (source) header.appendChild(source.cloneNode(true));
  }

  /* ================= the lead form ================= */
  function initForm(page) {
    var form = page.querySelector('#tt-lead-form');
    if (!form) return;
    // preferred day: today .. +120 days, in the visitor's own calendar
    var dateInput = form.querySelector('input[name="visitDate"]');
    if (dateInput) {
      var pad = function (n) {
        return (n < 10 ? '0' : '') + n;
      };
      var iso = function (d) {
        return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
      };
      var today = new Date();
      var max = new Date(today.getTime());
      max.setDate(max.getDate() + 120);
      dateInput.min = iso(today);
      dateInput.max = iso(max);
    }
  }

  function fieldError(form, name, message) {
    var input = form.elements[name];
    var err = form.querySelector('[data-tt-error="' + name + '"]');
    if (input) {
      if (message) input.setAttribute('aria-invalid', 'true');
      else input.removeAttribute('aria-invalid');
    }
    if (err) err.textContent = message || '';
  }

  function clearErrors(form) {
    ['firstName', 'lastName', 'phone', 'email'].forEach(function (n) {
      fieldError(form, n, '');
    });
    var status = form.querySelector('[data-tt-status]');
    if (status) status.textContent = '';
  }

  function failMessage(form) {
    var status = form.querySelector('[data-tt-status]');
    if (!status) return;
    var phone = form.getAttribute('data-phone-display') || '';
    var tel = form.getAttribute('data-phone-tel') || '';
    var msg = 'Sorry, we could not send your request just now.';
    if (phone) {
      status.textContent = '';
      status.appendChild(document.createTextNode(msg + ' Please call us at '));
      var a = document.createElement('a');
      a.href = 'tel:' + tel;
      a.textContent = phone;
      status.appendChild(a);
      status.appendChild(document.createTextNode(" and we'll get you scheduled."));
    } else {
      status.textContent = msg + ' Please try again in a moment.';
    }
  }

  function phoneDigits(v) {
    var d = String(v || '').replace(/\D/g, '');
    if (d.length === 11 && d.charAt(0) === '1') d = d.slice(1);
    return d;
  }

  function validate(form) {
    var ok = true;
    var first = null;
    var mark = function (name, msg) {
      fieldError(form, name, msg);
      if (!first) first = form.elements[name];
      ok = false;
    };
    if (!form.elements.firstName.value.trim()) mark('firstName', 'Please enter your first name.');
    if (!form.elements.lastName.value.trim()) mark('lastName', 'Please enter your last name.');
    if (phoneDigits(form.elements.phone.value).length !== 10) mark('phone', 'Please enter a 10-digit phone number.');
    var email = form.elements.email ? form.elements.email.value.trim() : '';
    if (email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) mark('email', 'Please check your email address, or leave it blank.');
    if (first) first.focus();
    return ok;
  }

  var sending = false;
  document.addEventListener('submit', function (e) {
    var form = closest(e.target, '#tt-lead-form');
    if (!form) return;
    e.preventDefault();
    if (sending) return;
    clearErrors(form);
    if (!validate(form)) return;
    if (!config.endpoint) {
      failMessage(form);
      return;
    }
    if (!leadEventId) leadEventId = newEventId('tt-lead');

    var body = new URLSearchParams();
    /* `model` and `store` land on the lead's Model and Store columns; `source` is the page's
       name, from which SPOS also reads the lead's product (Transformer Table). */
    ['firstName', 'lastName', 'phone', 'email', 'model', 'store', 'visitDate', 'visitTime', 'website', 'source'].forEach(function (n) {
      var el = form.elements[n];
      if (el) body.append(n, el.value.trim());
    });
    body.append('elapsedMs', String(Math.round(window.performance && performance.now ? performance.now() : 99999)));
    body.append('page', window.location.pathname);
    body.append('gclid', attribution.gclid || cookieGclid());
    body.append('gbraid', attribution.gbraid || '');
    body.append('wbraid', attribution.wbraid || '');
    body.append('utmSource', attribution.utm_source || '');
    body.append('utmMedium', attribution.utm_medium || '');
    body.append('utmCampaign', attribution.utm_campaign || '');
    body.append('utmTerm', attribution.utm_term || '');
    body.append('utmContent', attribution.utm_content || '');
    body.append('clickAgeDays', attributionAt ? String(Math.floor((Date.now() - attributionAt) / DAY_MS)) : '');
    body.append('referrer', entryReferrer.slice(0, 500));

    var submitBtn = form.querySelector('button[type="submit"]');
    var label = submitBtn ? submitBtn.textContent : '';
    sending = true;
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = 'Sending…';
    }

    var controller = 'AbortController' in window ? new AbortController() : null;
    // 30s: the endpoint has taken ~10s end to end, and a timeout here tells the customer to
    // call even when the lead was saved -- wait generously rather than cry wolf.
    var timer = setTimeout(function () {
      if (controller) controller.abort();
    }, 30000);

    // A form-urlencoded POST with no custom headers is a CORS "simple request": no preflight.
    fetch(config.endpoint, {
      method: 'POST',
      body: body,
      mode: 'cors',
      credentials: 'omit',
      signal: controller ? controller.signal : undefined,
    })
      .then(function (res) {
        return res
          .json()
          .catch(function () {
            return {};
          })
          .then(function (data) {
            return { status: res.status, data: data || {} };
          });
      })
      .then(function (r) {
        if (r.status === 200 && r.data.ok) {
          onSuccess(form);
          return;
        }
        if (r.status === 400 && r.data.field && form.elements[r.data.field]) {
          fieldError(form, r.data.field, r.data.message || 'Please check this field.');
          form.elements[r.data.field].focus();
          return;
        }
        failMessage(form);
      })
      .catch(function () {
        failMessage(form);
      })
      .then(function () {
        clearTimeout(timer);
        sending = false;
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.textContent = label;
        }
      });
  });

  function onSuccess(form) {
    var firstName = form.elements.firstName.value.trim();
    if (adsReady && config.enhanced) {
      var userData = { phone_number: '+1' + phoneDigits(form.elements.phone.value) };
      var email = form.elements.email ? form.elements.email.value.trim() : '';
      if (email) userData.email = email;
      window.gtag('set', 'user_data', userData);
    }
    fireConversion(config.leadLabel);
    measure('lead_created', { type: 'customer_action' }, { event_id: leadEventId });
    if (window.dataLayer) window.dataLayer.push({ event: 'tt_lead_submitted' });

    var success = document.getElementById('tt-lead-success');
    if (success) {
      var nameSlot = success.querySelector('[data-tt-first-name]');
      if (nameSlot) nameSlot.textContent = firstName ? ', ' + firstName : '';
      form.hidden = true;
      success.hidden = false;
      success.focus();
    }
  }

  /* ================= start ================= */
  function init() {
    placeHeaderCta();
    document.querySelectorAll('[data-tt-page]').forEach(function (page) {
      page.querySelectorAll('[data-tt-diagram]').forEach(function (diagram) {
        drawDiagram(diagram, false);
      });
      initVideo(page);
      initSlotVideos(page);
      initSticky(page);
      initForm(page);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  // the theme editor swaps the section's markup in place
  document.addEventListener('shopify:section:load', init);
})();
